import "./style.scss";
import { FC, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import { setColumnWidth, toggleRowSelection, toggleAllRows, setActualFieldValue } from "@/src/store/slices/TasksPage/tasksTableSlice";
import { tasksStarsConst, tasksStatusConst } from "@/src/constants/tasksConsts";
import { getTasksColumnCellValue } from "@/src/constants/tasksTableColumns";
import { IFiltersList, IRowData } from "@/src/types/applicationOnline";
import { ITasksTableRow } from "@/src/types/tasksTable";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import ArrowSvg from "@/src/svg/applicationsPage/APPopUp/Documents/ArrowSvg";
import TasksFilterPopUp from "../TasksFilterPopUp/TasksFilterPopUp";
import VerticalLine from "../../VerticalLine/VerticalLine";
import { ITaskUpdatePayload, tasksPageRequests } from "@/src/api/tasksPageRequests";
import { applicantHistoryRequests } from "@/src/api/applicantHistoryRequests";
import GuidelineSvg from "@/src/svg/sidebar/GuidelineSvg";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";

interface TasksBodyProps {
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDataPopUp: React.Dispatch<React.SetStateAction<IRowData["userInfo"] | undefined>>;
  setCurrentStudentId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setCurrentApplyInfoId: React.Dispatch<React.SetStateAction<number | undefined>>;
  tableFiltersList: IFiltersList | undefined;
  isShowSettingsModal: boolean;
  taskIdMap: Record<number, string>;
  onTaskUpdated?: (taskRowId: number, patch: { status: string }) => void;
}

const TasksBody: FC<TasksBodyProps> = ({
  headerCheckboxRef,
  setIsApPopUpFullName,
  setCurrentDataPopUp,
  setCurrentStudentId,
  setCurrentApplyInfoId,
  tableFiltersList,
  isShowSettingsModal,
  taskIdMap,
  onTaskUpdated,
}) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const { visibleColumns, data, selectedRows } = useSelector((state: RootState) => state.tasksTable);
  const safeData = Array.isArray(data) ? data : [];
  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState<string>("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);
  const allChecked = selectedRows.length === safeData.length && safeData.length > 0;
  const someChecked = selectedRows.length > 0 && selectedRows.length < safeData.length;
  const [fixedColumnIndex, setFixedColumnIndex] = useState<number | null>(null);
  const [isAttach, setIsAttach] = useState<number | null>(null);
  const CHECKER_WIDTH = 4.12;

  const handleAttach = (index: number) => {
    if (isShowSettingsModal) {
      setFixedColumnIndex((prev) => (prev === index ? null : index));
      setIsAttach(index);
      if (isAttach === index) setIsAttach(null);
    }
  };

  const getStickyStyles = (index: number, section: "thead" | "tbody") => {
    if (fixedColumnIndex === null || index > fixedColumnIndex) return {};
    const left = CHECKER_WIDTH + visibleColumns.slice(0, index).reduce((acc, col) => acc + (col.width || 6), 0);
    return { position: "sticky" as const, left: `${left}rem`, zIndex: section === "thead" ? 7 : 5 };
  };

  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({ startX: 0, thId: null, startWidth: 0 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrollStartX = useRef(0);
  const scrollStartScrollLeft = useRef(0);

  const isTheadScrollTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      !target.closest("input") &&
      !target.closest(".custom-checkbox") &&
      !target.closest(".cell-wrapper-arrow") &&
      !target.closest(".cell-wrapper-attach") &&
      !target.closest(".vertical-line") &&
      !target.closest(".tasks-page-filter")
    );
  };

  const onTheadScrollMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const dx = e.clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const stopTheadScrollDrag = () => {
    isDragging.current = false;
  };

  const onTheadScrollTouchStart = (e: React.TouchEvent) => {
    if (!scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.touches[0].clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
    if (!isShowSettingsModal) return;
    e.stopPropagation();
    e.preventDefault();
    dragData.current = { startX: e.clientX, thId, startWidth };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isShowSettingsModal || !dragData.current.thId) return;
    const deltaRem = (e.clientX - dragData.current.startX) / 16;
    let newWidth = dragData.current.startWidth + deltaRem;
    if (newWidth < 2) newWidth = 2;
    dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
  };

  const onMouseUp = () => {
    if (!isShowSettingsModal) return;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  const handleTableFullNameEvent = async (value: ITasksTableRow) => {
    setCurrentStudentId(value.student_id);
    setCurrentApplyInfoId(value.task_id);
    try {
      const userInfo = await fetchApplicantUserInfo(value.student_id);
      if (userInfo) setCurrentDataPopUp(userInfo);
    } catch (error) {
      console.log("applicant request error:", error);
    }
    setIsApPopUpFullName(true);
  };

  const handleEventChangeFieldValue = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number,
    value: string,
    actualPopUp: string,
  ) => {
    event.stopPropagation();
    setOpenTableParamMenuRowId(null);
    setOpenTableParamMenu("");
    const actualFieldForChange = actualPopUp === "star" ? "color_star" : actualPopUp === "status" ? "status" : undefined;
    if (!actualFieldForChange) return;
    const targetRow = safeData.find((item) => item.task_id === id);
    const isStatusChange = actualPopUp === "status";
    const previousStatus = targetRow?.status;
    const nextStatus = value;
    const nextStatusLabel = tasksStatusConst.find((item) => item.id === nextStatus)?.label ?? nextStatus;

    if (isStatusChange && previousStatus === nextStatus) return;
    if (!targetRow) return;

    if (actualFieldForChange === "color_star") {
      dispatch(setActualFieldValue({ id, fieldActualValue: value, keyField: actualFieldForChange }));
      notify({
        title: "Star updated",
        message: "Task marker updated.",
        type: "success",
      });
      return;
    }

    try {
      const taskId = taskIdMap[id] ?? String(id);
      await tasksPageRequests.update(taskId, { status: value });

      dispatch(setActualFieldValue({ id, fieldActualValue: value, keyField: actualFieldForChange }));
      onTaskUpdated?.(id, { status: value });

      if (targetRow.student_id != null) {
        await applicantHistoryRequests.create({
          applicant_id: targetRow.student_id,
          text: `Changes have been made to status updated to "${nextStatusLabel}"`,
        });
      }
      notify({
        title: "Status updated",
        message: `Task status changed to "${nextStatusLabel}".`,
        type: "success",
      });
    } catch (error) {
      console.error("Update error:", error);
      notify({
        title: "Update failed",
        message: "Could not update task. Please try again.",
        type: "error",
      });
    }
  };

  const toggleTableParamMenu = (menuType: string, rowId: number) => {
    if (openTableParamMenu === menuType && openTableParamMenuRowId === rowId) {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);
    } else {
      setOpenTableParamMenu(menuType);
      setOpenTableParamMenuRowId(rowId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-menu") && !target.closest(".star-menu")) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const replacingBackendValueToCustom: Record<string, string> = { APPLY_ONLINE: "Apply Online", NEW: "New" };

  return (
    <div
      className="tasks-page-body"
      ref={scrollRef}
      onMouseMove={onTheadScrollMouseMove}
      onMouseUp={stopTheadScrollDrag}
      onMouseLeave={stopTheadScrollDrag}
      onTouchMove={onTheadScrollTouchMove}
      onTouchEnd={stopTheadScrollDrag}
      onTouchCancel={stopTheadScrollDrag}
    >
      <table>
        <thead onMouseDown={onTheadScrollMouseDown} onTouchStart={onTheadScrollTouchStart}>
          <tr>
            <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}>
                  <input type="checkbox" checked={allChecked} ref={headerCheckboxRef} onChange={() => dispatch(toggleAllRows())} />
                  <span className="checkmark"></span>
                </label>
                <div
                  className={`cell-wrapper-arrow ${filterPopUp === "color_star" ? "rotate" : ""}`}
                  onClick={() => setFilterPopUp(filterPopUp === "color_star" ? null : "color_star")}
                >
                  <ArrowFilter />
                </div>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
                <TasksFilterPopUp
                  id="color_star"
                  tableFiltersList={tableFiltersList}
                  filterPopUp={filterPopUp}
                  setFilterPopUp={setFilterPopUp}
                />
              </div>
            </th>
            {visibleColumns.map(({ title, id, width }, index) => (
              <th key={id} style={{ width: `${width}rem`, minWidth: `${width}rem`, ...getStickyStyles(index, "thead") }}>
                <div className="cell-wrapper">
                  <p>{title}</p>
                  <div className="cell-wrapper-actions">
                    {(isShowSettingsModal || isAttach === index) && (
                      <div className="cell-wrapper-attach" onClick={() => handleAttach(index)}>
                        <GuidelineSvg />
                      </div>
                    )}
                    {(id === "date" ||
                      id === "updated" ||
                      id === "status" ||
                      id === "type" ||
                      id === "country" ||
                      id === "course" ||
                      id === "assignee" ||
                      id === "assigneeId") && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === id ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === id ? null : id)}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                  </div>
                  <div className="vertical-line" onMouseDown={(e) => onMouseDown(e, id, width || 6)} style={{ cursor: "ew-resize" }}>
                    <VerticalLine />
                  </div>
                  <TasksFilterPopUp id={id} tableFiltersList={tableFiltersList} filterPopUp={filterPopUp} setFilterPopUp={setFilterPopUp} />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {safeData.length === 0 && (
            <tr>
              <td colSpan={visibleColumns.length + 1}>
                <div className="cell-wrapper">There is nothing here right now.</div>
              </td>
            </tr>
          )}
          {safeData.map((row) => (
            <tr key={row.task_id} className={row.is_read ? "" : "form-not-read"}>
              <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
                <div className="cell-wrapper">
                  <label className="custom-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(row.task_id)}
                      onChange={() => dispatch(toggleRowSelection(row.task_id))}
                    />
                    <span className="checkmark"></span>
                  </label>
                  <div
                    className={`star-icon ${row.color_star}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTableParamMenu("star", row.task_id);
                    }}
                  >
                    {tasksStarsConst
                      .filter((item) => row.color_star === item.color)
                      .map((item) => (
                        <item.Icon key={item.color} />
                      ))}
                  </div>
                  {openTableParamMenu === "star" && openTableParamMenuRowId === row.task_id && (
                    <div className="star-menu">
                      {tasksStarsConst.map(({ color, Icon, description }) => (
                        <div
                          key={color}
                          className={`star-option ${color}`}
                          onClick={(e) => handleEventChangeFieldValue(e, row.task_id, color, "star")}
                        >
                          <Icon />
                          {description && description !== "" && <div className="tooltip">{description}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="vertical-line">
                    <VerticalLine />
                  </div>
                </div>
              </td>
              {visibleColumns.map(({ id, width }, index) => {
                const rawValue = getTasksColumnCellValue(row, id);
                const displayValue = replacingBackendValueToCustom[rawValue] ?? rawValue;
                return (
                  <td key={id} style={{ width: `${width}rem`, minWidth: `${width}rem`, ...getStickyStyles(index, "tbody") }}>
                    <div
                      className={`cell-wrapper ${id === "fullName" || id === "status" ? "clicable" : ""}`}
                      {...(id === "fullName" && { onClick: () => handleTableFullNameEvent(row) })}
                      {...(id === "status" && {
                        onClick: (e) => {
                          e.stopPropagation();
                          toggleTableParamMenu("status", row.task_id);
                        },
                      })}
                    >
                      {id === "status"
                        ? tasksStatusConst
                            .filter((item) => row.status === item.id)
                            .map((item) => (
                              <div key={item.id} className={`status-option ${item.id}`}>
                                <span className="status-label">{item.label}</span>
                              </div>
                            ))
                        : null}
                      {id !== "status" ? (
                        id === "fullName" ? (
                          <div className="full-name-cell">
                            <p>{displayValue}</p>
                            <ArrowSvg />
                          </div>
                        ) : (
                          <div>{displayValue}</div>
                        )
                      ) : null}
                      {id === "status" && openTableParamMenu === "status" && openTableParamMenuRowId === row.task_id ? (
                        <div className="status-menu">
                          {tasksStatusConst.map(({ label, id: statusId }) => (
                            <div
                              key={statusId}
                              className={`status-option ${statusId}`}
                              onClick={(e) => handleEventChangeFieldValue(e, row.task_id, statusId, "status")}
                            >
                              <span className="status-label">{label}</span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <div className="vertical-line">
                        <VerticalLine />
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TasksBody;
